// The ONE place a GET /api/auth/session response becomes store state. Used by
// login, page reload, entering/leaving enterprise mode, Manage (reseller
// impersonation) and leaving impersonation, so the session mode is always
// rebuilt from the server's claims and never from client-side memory.
import { sessionActions, orgActions, sultanActions } from '@/store';
import { auth, enterprise as enterpriseApi, getAuthToken, setAuthToken } from '@/lib/trackizy';
import api from '@/lib/api';
import { enterpriseOrg } from './enterpriseSettings';

/** `{ mode, returnMode }` from the server's session claims. */
export function deriveSessionMode(sessionData) {
  if (sessionData?.enterprise) return { mode: 'enterprise', returnMode: 'normal' };
  if (sessionData?.impersonation) {
    return {
      mode: 'impersonated',
      returnMode: sessionData.impersonation.returnMode === 'enterprise' ? 'enterprise' : 'normal',
    };
  }
  return { mode: 'normal', returnMode: 'normal' };
}

/**
 * A reseller member with no organization of their own (and no instance role)
 * has nothing to see in normal mode: their home is the enterprise view.
 */
export function needsEnterpriseAutoEnter(sessionData) {
  return Boolean(
    sessionData?.reseller
    && !sessionData.org
    && !sessionData.enterprise
    && !sessionData.impersonation
    && !sessionData.is_sultan
    && !sessionData.instance,
  );
}

export function applySession(sessionData, dispatch, { server } = {}) {
  const { mode, returnMode } = deriveSessionMode(sessionData);
  // `administrator` shim for Traccar-era call sites that still read
  // user.administrator; sultans and org admins get it, enterprise never does.
  const orgRole = sessionData.org?.role;
  const isAdmin = Boolean(sessionData.is_sultan) || orgRole === 'admin';
  dispatch(sessionActions.updateUser({ ...sessionData.user, administrator: isAdmin }));
  if (server !== undefined) dispatch(sessionActions.updateServer(server));
  dispatch(sessionActions.updateExpiry(sessionData.expiry || null));
  dispatch(sessionActions.updateModules(sessionData.modules || []));
  dispatch(sessionActions.updatePlatformBasemaps(sessionData.platformBasemaps || []));
  dispatch(sessionActions.updateIsHub(sessionData.isHub));
  dispatch(sessionActions.updateReseller(sessionData.reseller || null));
  dispatch(sessionActions.updateInstance(sessionData.instance || null));

  // Bumps the scope generation and empties every tenant slice BEFORE the new
  // org lands, so nothing from the previous scope survives the switch.
  dispatch(sessionActions.enterScope({ mode, returnMode, enterprise: sessionData.enterprise ?? null }));

  if (mode === 'enterprise') {
    const org = enterpriseOrg(sessionData.enterprise);
    dispatch(orgActions.setOrg({ org, role: org.role, token: null }));
  } else if (sessionData.org) {
    const { role, ...orgData } = sessionData.org;
    dispatch(orgActions.setOrg({ org: orgData, role, token: null }));
  } else {
    dispatch(orgActions.clearOrg());
  }

  if (sessionData.impersonation) {
    dispatch(sultanActions.startImpersonation(sessionData.impersonation));
  } else {
    dispatch(sultanActions.exitImpersonation());
  }
  dispatch(sultanActions.setSultan(Boolean(sessionData.is_sultan)));
  return { mode, returnMode };
}

/**
 * Swap to a freshly minted token, then rebuild the store from its session. If
 * the session read fails, the previous token goes back (like fetchSession): the
 * store still describes the old session, and the stored token must match it.
 */
export async function adoptToken(token, dispatch) {
  const previousToken = getAuthToken();
  setAuthToken(token);
  let sessionData;
  try {
    sessionData = await auth.session();
  } catch (err) {
    setAuthToken(previousToken);
    throw err;
  }
  applySession(sessionData, dispatch);
  return sessionData;
}

/**
 * GET the session for the stored token — entering enterprise mode first (fresh
 * token) when the user is a reseller member with nowhere else to land. The
 * caller applies the result with applySession.
 */
export async function fetchSession() {
  const sessionData = await auth.session();
  if (!needsEnterpriseAutoEnter(sessionData)) return sessionData;
  const previousToken = getAuthToken();
  let swapped = false;
  try {
    const { token } = await enterpriseApi.enter();
    setAuthToken(token);
    swapped = true;
    return await auth.session();
  } catch {
    // Reseller suspended or membership gone: keep the plain session. If the
    // token was already swapped, put the old one back — the store is about to
    // be built from the plain session, and the stored token must match it.
    if (swapped) setAuthToken(previousToken);
    return sessionData;
  }
}

/**
 * The legacy server-settings read for the shell. Enterprise sessions have no
 * access to it (403), so it is skipped there and `undefined` leaves the store's
 * server slice alone (applySession only writes it when defined).
 */
export async function fetchShellServer(sessionData) {
  if (sessionData?.enterprise) return undefined;
  return api.getServer().catch(() => ({}));
}
